import Button from './Button';
import styled, { CSSObject } from '@emotion/styled';
import { getButtonTheme, getButtonIcons } from '../../style';
import { getBrowserDetails, isDarkMode } from '../../utils/helpers';
import breakpoints from '../../theme/material/breakpoints';
import { CustomStyledProps } from 'style/style';

const { isSafari } = getBrowserDetails();

const ButtonContainer = styled('div')(
  (): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: 'max-content',
  })
);

const DefaultButton = styled('button')<{ isFavorited: boolean; page: string }>(
  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
  () => ({
    color: `${getButtonTheme().darkReverse} !important`,
    padding: 0,
    background: isDarkMode() ? '#212121' : '#F6F4F4',
    borderRadius: '50%',
    width: 55,
    height: 55,
    border: getButtonIcons().borderStyle,
    filter: 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.16))',
    position: 'relative',
    display: 'block',
    outline: 0,
    boxSizing: 'border-box',
    [breakpoints.down(768.05)]: {
      padding: 0,
      width: 40,
      height: 40,
      borderWidth: 2,
    },
    [breakpoints.down(639.05)]: {
      padding: 0,
      width: 40,
      height: 40,
      borderWidth: 2,
    },
    '+label': {
      display: 'block',
      fontSize: 12,
      fontWeight: 400,
      letterSpacing: -0.3,
      color: isDarkMode() ? '#AFB0B1' : '#8F8F8F',
    },
    margin: '5px 10px',
    [breakpoints.between(769, 801)]: {
      margin: 5,
    },
  }),
  ({ isFavorited }: CustomStyledProps) =>
    isFavorited && {
      color: '#E21D4A !important',
      border: getButtonIcons().borderStyle,
    },
  ({ page }: CustomStyledProps) =>
    page &&
    (page === 'viewed_me' || page === 'from') && {
      margin: '5px 10px',
      [breakpoints.down(639.05)]: {
        margin: '5px 10px',
      },
    }
);

const CustomButton = styled(DefaultButton)`
  &:hover {
    color: #fff !important;
    background: #e21d4a !important;
  }

  &:hover + label {
    color: #e21d4a;
  }

  &:hover svg path {
    fill: #ffffff;
  }
`;

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const DeclineFavoriteButton = styled(DefaultButton)(() => ({
  '&:hover': {
    color: '#E21D4A !important',
    background: '#E21D4A !important',
    '&::before': {
      borderBottom: '2px solid #F6F4F4',
    },
  },

  '&:hover + label': {
    color: '#E21D4A',
  },

  '&:hover svg path': {
    fill: '#E21D4A',
    stroke: '#F6F4F4',
    strokeWidth: 2,
  },

  '&::before': {
    content: "''",
    position: 'absolute',
    borderBottom: getButtonIcons().borderCrashOutStyle,
    transform: 'translateY(25px) translateX(10px) rotate(318deg)',
    top: '-49px',
    left: '-30px',
    width: '54px',
    height: '54px',
    zIndex: 1,

    [breakpoints.down(768.05)]: {
      top: '-43px',
      left: '-24px',
      width: '40px',
      height: '40px',
      borderBottomWidth: '2px',
    },
    [breakpoints.down(639.05)]: {
      top: '-43px',
      left: '-24px',
      width: '40px',
      height: '40px',
      borderBottomWidth: '2px',
    },
    '@media (max-width: 550px)': {
      top: '-43px',
      left: '-26px',
      width: '40px',
      height: '40px',
      borderBottomWidth: '2px',
    },
  },
}));

const IconContainer = styled('div')`
  display: flex;
  & > svg {
    height: 38.21px;
  }

  ${breakpoints.down(768.05)} {
    margin-right: 0px;
    & > svg {
      height: 28.21px;
    }
  }
  ${breakpoints.down(639.05)} {
    margin-right: 0px;
    & > svg {
      height: 28.21px;
    }
  }
`;

const ButtonText = styled('div')`
  display: block;
  align-items: center;
  font-family:
    system-ui,
    -apple-system,
    Arial,
    sans-serif;
  ${breakpoints.down(639.05)} {
    display: none;
  }
`;

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const ButtonWrap = styled(Button)`
  ${breakpoints.down(639.05)} {
    display: none;
  }
`;

const DismissButton = styled(DefaultButton)`
  &:hover {
    color: rgba(149, 199, 235, 1) !important;
    background: #3e79a5 !important;
  }

  &:hover + label {
    color: #3e79a5;
  }

  &:hover svg path {
    fill: rgba(255, 255, 255, 1);
  }
`;

const DismissIconContainer = styled('div')`
  margin-top: 5px;

  & > svg {
    fill: #05c4c1;
    height: 24px;
    width: 24px;
  }

  ${breakpoints.down(768.05)} {
    margin-right: 0px;
    & > svg {
      height: 17px;
    }
  }

  ${breakpoints.down(639.05)} {
    margin-right: 0px;
    & > svg {
      height: 17px;
    }
  }
`;

const OfferNewButton = styled(DefaultButton)`
  &:hover {
    color: #e2c92d !important;
    background: #e2c92d !important;
  }

  &:hover + label {
    color: #e2c92d;
  }

  &:hover svg path {
    fill: rgba(255, 255, 255, 1);
  }
`;

const OfferIconContainer = styled('div')`
  & > svg {
    fill: ${isDarkMode() ? '#E2C92D' : '#05c4c1'};
    height: 31px;
    width: 17px;
  }

  ${breakpoints.down(768.05)} {
    margin-right: 0px;
    & > svg {
      height: 20px;
    }
  }
  ${breakpoints.down(639.05)} {
    margin-right: 0px;
    & > svg {
      height: 20px;
    }
  }
`;

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const OfferDeclineButton = styled(DefaultButton)(() => ({
  '&:hover': {
    color: '#E21D4A !important',
    background: '#E21D4A !important',
    '&::before': {
      borderBottom: '2px solid #fff',
    },
  },

  '&:hover + label': {
    color: '#E21D4A',
  },

  '&:hover svg path': {
    fill: '#EB6180',
  },

  '&::before': {
    content: "''",
    position: 'absolute',
    borderBottom: getButtonIcons().borderCrashOutStyle,
    transform: 'translateY(25px) translateX(10px) rotate(-44deg)',
    top: '-47px',
    left: '-30px',
    width: '54px',
    height: '54px',
    zIndex: 1,

    [breakpoints.down(768.05)]: {
      top: '-43px',
      left: '-24px',
      width: '40px',
      height: '40px',
      borderBottomWidth: '2px',
    },
    [breakpoints.down(639.05)]: {
      top: '-43px',
      left: '-24px',
      width: '40px',
      height: '40px',
      borderBottomWidth: '2px',
    },
    '@media (max-width: 550px)': {
      top: '-43px',
      left: '-26px',
      width: '40px',
      height: '40px',
      borderBottomWidth: '2px',
    },
  },
}));

const OfferDeclineIconContainer = styled('div')`
  & > svg {
    fill: ${isDarkMode() ? '#E84A6E' : '#EB6180'};
    height: 36px;
    width: 36px;
    position: absolute;
    top: 54%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  ${breakpoints.down(639.05)} {
    margin-right: 0px;
    & > svg {
      height: 20px;
      width: 30px;
      position: absolute;
      top: 54%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }
`;

const OfferCancelButton = styled(DefaultButton)`
  &:hover {
    color: #e21d4a !important;
    background: #e21d4a !important;
  }

  &:hover + label {
    color: #e21d4a !important;
  }

  &:hover svg path {
    fill: rgba(255, 255, 255, 1);
  }
`;

const OfferCancelIconContainer = styled('div')`
  & > svg {
    fill: #E21D4A;
    height: 24px;
    width: 24px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  ${breakpoints.down(768.05)} {
    margin-right: 0px;
    & > svg {
      height: 17px;
    }

  ${breakpoints.down(639.05)} {
    margin-right: 0px;
    & > svg {
      height: 17px;
    }
  }
`;

const OfferAcceptButton = styled(DefaultButton)`
  &:hover {
    color: rgba(36, 168, 58, 1) !important;
    background: rgba(36, 168, 58, 1) !important;
  }

  &:hover + label {
    color: #24a83a;
  }

  &:hover svg path {
    fill: rgba(255, 255, 255, 1);
  }
`;

const OfferAccepIconContainer = styled('div')`
  display: flex;

  & > svg {
    fill: #24a83a;
    height: 36px;
    width: ${isSafari ? '36px' : 'auto'};
  }

  ${breakpoints.down(768.05)} {
    margin-right: 0px;
    & > svg {
      height: 20px;
    }
  }
  ${breakpoints.down(639.05)} {
    margin-right: 0px;
    & > svg {
      height: 20px;
    }
  }
`;

const OfferCounterButton = styled(DefaultButton)`
  &:hover {
    color: #05c4c1 !important;
    background: #05c4c1 !important;
  }

  &:hover + label {
    color: #05c4c1;
  }

  &:hover svg path {
    fill: rgba(255, 255, 255, 1);
  }
`;

const OfferCounterIconContainer = styled('div')`
  display: flex;

  & > svg {
    fill: ${isDarkMode() ? '#34CABB' : '#05C4C1'};
    height: 36px;
    width: 36px;
  }

  ${breakpoints.down(639.05)} {
    margin-right: 0px;
    & > svg {
      height: 27px;
    }
  }
`;

const OfferMessageIconContainer = styled('div')`
  display: flex;

  & > svg {
    fill: ${isDarkMode() ? '#2B8FD7' : '#05c4c1'};
    height: 28px;
    width: 28px;
  }

  ${breakpoints.down(639.05)} {
    margin-right: 0px;
    & > svg {
      height: 17px;
    }
  }
`;

export {
  ButtonContainer,
  DefaultButton,
  CustomButton,
  DeclineFavoriteButton,
  IconContainer,
  ButtonText,
  ButtonWrap,
  OfferNewButton,
  OfferIconContainer,
  OfferDeclineButton,
  OfferDeclineIconContainer,
  OfferCancelButton,
  OfferCancelIconContainer,
  OfferAcceptButton,
  OfferAccepIconContainer,
  DismissButton,
  DismissIconContainer,
  OfferCounterButton,
  OfferCounterIconContainer,
  OfferMessageIconContainer,
};
